import React from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { Project } from '../../../types/domain';

interface ProjectDangerCardProps {
  currentProject: Project | null;
  deleteModalOpen: boolean;
  setDeleteModalOpen: (open: boolean) => void;
  deleteConfirmText: string;
  setDeleteConfirmText: (text: string) => void;
  isDeleting: boolean;
  handleDeleteProject: () => void;
}

export const ProjectDangerCard: React.FC<ProjectDangerCardProps> = ({
  currentProject,
  deleteModalOpen,
  setDeleteModalOpen,
  deleteConfirmText,
  setDeleteConfirmText,
  isDeleting,
  handleDeleteProject,
}) => {
  return (
    <>
      <Card className="p-5 bg-rose-500/5 border-rose-500/30 space-y-4 shadow-xs">
        <h2 className="text-sm font-semibold text-rose-400 flex items-center gap-2">
          <Trash2 size={16} />
          <span>Delete Project</span>
        </h2>

        <p className="text-xs text-cf-textSecondary leading-relaxed">
          Deleting this project permanently deletes all associated datastore tables, submissions, and notification configurations. This action cannot be undone.
        </p>

        <div className="pt-2">
          <Button
            variant="danger"
            size="xs"
            onClick={() => setDeleteModalOpen(true)}
            leftIcon={<Trash2 size={13} />}
          >
            Delete This Project
          </Button>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Project Deletion"
      >
        <div className="space-y-4">
          <p className="text-xs text-cf-textSecondary">
            To confirm deletion, please enter the project slug{' '}
            <code className="text-rose-400 font-bold">{currentProject?.slug}</code> below:
          </p>

          <Input
            size="xs"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={currentProject?.slug}
            className="border-rose-500/40"
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="xs"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="xs"
              disabled={isDeleting || deleteConfirmText !== currentProject?.slug}
              onClick={handleDeleteProject}
              leftIcon={isDeleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            >
              Delete Permanently
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
